/**
 * GrecaptchaV2_JS options.
 * @typedef {Object} GRECAPTCHA_OPTIONS
 * @property {String} selector HTML parent container.
 * @property {String} siteKey recaptcha render Id.
 * @property {String} theme Color Theme of thw widget: ('dark','light').
 * @property {String} size The size of the widget ('compact','normal','invisible').
 * @property {Number} tabindex  The tabindex of the widget and challenge.
 * @property {Function} callback The name of your callback function, executed when the user submits a successful response. The g-recaptcha-response token is passed to your callback.
 * @property {Function} expiredCallback The name of your callback function, executed when the reCAPTCHA response expires and the user needs to re-verify.
 * @property {Function} errorCallback The name of your callback function, executed when reCAPTCHA encounters an error (usually network connectivity) and cannot continue until connectivity is restored. 
 * @property {String} badge (Invisible only) position of the recaptcha. ('bottomright','bottomleft','inline').
 * @property {Boolean} isolated  (Invisible only)  If true, this reCAPTCHA instance will be part of a separate ID space.
 * @property {Boolean} isInvisible  True for an invisible recaptcha.
 * @property {HTMLInputElement} inputForm Input HTML Element that receives token value after a successful response.
 */

/**
 * Google recaptcha V2 object.
 */
class GrecaptchaV2_JS {
    /**
     * @param {Object} pGrecaptcha grecaptcha object.
     * @param {GRECAPTCHA_OPTIONS} pOptions
     * @constructor
     */
    constructor(pGrecaptcha, pOptions) {

        if (!pGrecaptcha) {
            throw new Error('Missing grecaptcha object.');
        }
        /**
         * @type {GRECAPTCHA_OPTIONS} 
         */
        let objOptions = {
            selector: null,
            siteKey: null,
            theme: null,
            size: null,
            tabindex: null,
            callback: null,
            expiredCallback: null,
            errorCallback: null,
            badge: null,
            isolated: false,
            isInvisible: false,
            inputForm: null
        }

        Object.assign(objOptions, pOptions);

        if (!objOptions.selector || !objOptions.siteKey) {
            throw new Error('Missing params: selector or sitekey for Google grecaptcha');
        }

        /**
         * Flag for invisble recaptcha.
         * @type {Boolean}
         * @public
         */
        this.IsInvisible = objOptions.isInvisible;
        /**
         * Html input that stores recaptcha value.
         * @type {HTMLInputElement} 
         * @public
         */
        this.InputForm = objOptions.inputForm;
        /**
         * grecaptcha object.
         * @type {Object} 
         * @public
         */
        this.Grecaptcha = pGrecaptcha;
        /**
         * Widget Id.
         * @type {Number} 
         * @public
         */
        this.widget_id = null;
        /**
         * Call back function on recaptcha response.
         * @type {Function} 
         * @public
         */
        this.FnCallback = objOptions.callback;
        /**
         * Call back function on expired recaptcha.
         * @type {Function} 
         * @public
         */
        this.FnexpiredCallback = objOptions.expiredCallback;
        /**
         * Recaptcha value.
         * @type {String} 
         * @public
         */
        this.RecaptchaValue = '';

        // Initialize recaptcha.
        this.init(objOptions);
    }
    // #region PUBLIC PROPERTIES
    /**
     * setter Recaptcha value.
     * @type {string}
     * @public 
     */
    set RecaptchaValue(pValue) {
        this._RecaptchaValue = pValue;
        // set input HTML value equal to RecaptchaValue
        if (this.InputForm) {
            this.InputForm.value = pValue;
            this.InputForm.classList.remove('is-invalid');
            this.InputForm.classList.add('is-valid');
            let errLabel =  this.InputForm.parentElement.querySelector(`.invalid-feedback[data-valmsg-for="${this.InputForm.name}"]`);
            !!errLabel && (errLabel.innerHTML = '');
        }
    }
    /**
     * getter current recaptcha value.
     * @type {string} 
     * @public
     */
    get RecaptchaValue() {
        return this._RecaptchaValue;
    }
    // #endregion
    /**
     * Initialize method.
     * @param {GRECAPTCHA_OPTIONS} pObjOptions GrecaptchaV2_JS options.
     * @private
     */
    init(pObjOptions) {
        if (this.IsInvisible) {
            this.widget_id = this.Grecaptcha.render(pObjOptions.selector, {
                'sitekey': pObjOptions.siteKey,
                'badge': !!pObjOptions.badge ? pObjOptions.badge : 'bottomright',
                'size': 'invisible',
                'tabindex': !!pObjOptions.tabindex ? pObjOptions.tabindex : 0,
                'callback': this.responseCallback.bind(this),
                'expired-callback': this.expiredCallBack.bind(this),
                'error-Recaptchacallback': !!pObjOptions.errorCallback ? pObjOptions.errorCallback : null,
                'isolated': pObjOptions.isolated
            });
        } else {
            this.widget_id = this.Grecaptcha.render(pObjOptions.selector, {
                'sitekey': pObjOptions.siteKey,
                'theme': !!pObjOptions.theme ? pObjOptions.theme : 'light',
                'size': !!pObjOptions.size ? pObjOptions.size : 'normal',
                'tabindex': !!pObjOptions.tabindex ? pObjOptions.tabindex : 0,
                'callback': this.responseCallback.bind(this),
                'expired-callback': this.expiredCallBack.bind(this),
                'error-callback': !!pObjOptions.errorCallback ? pObjOptions.errorCallback : null
            });
        }
    }
    // #region PUBLIC METHODS
    /**
     * Update RecaptchaValue and call FnCallback property.
     * @param {String} ptoken 
     */
    responseCallback(ptoken) {
        this.RecaptchaValue = ptoken;
        if (!!this.FnCallback) {
            this.FnCallback(ptoken);
        }
    }
    /**
     * Reset Recaptcha value and call FnexpiredCallback property,
     */
    expiredCallBack() {
        this.reset();
        this.InputForm.value = '';
        this.InputForm.classList.remove('is-valid');
        this.InputForm.classList.add('is-invalid');
        if (!!this.FnexpiredCallback) {
            this.FnexpiredCallback();
        }
    }
    /**
     * Compute Recaptcha response value.
     * @returns {String} Recaptcha value
     */
    getResponse() {
        if (this.IsInvisible) {
            this.Grecaptcha.execute(this.widget_id);
        } else {
            this.RecaptchaValue = this.Grecaptcha.getResponse(this.widget_id);
        }
        return this.RecaptchaValue;
    }
    /**
     * Reset Recaptcha.
     */
    reset() {
        this.Grecaptcha.reset(this.widget_id);
        this.RecaptchaValue = '';
    }
    /**
     * Evaluate validity of recaptcha.
     * @returns {boolean} true for valid recaptcha.
     */
    isValid() {
        return this.RecaptchaValue.length > 0;
    }
    // #endregion
    //#region STATIC METHODS
    /**
     * @description Add Google Recaptcha library to the DOM.
     * @param {Function} pFnOnload - A callback that Google executes after Load all of the resources.
     * @param {Boolean} pIsAsync - True for script tag async attribute
     * @param {Boolean} pIsDefer - True for script tag defer attribute
     */
    static addRecaptchaLibrary(pFnOnload, pIsAsync = false, pIsDefer = false) {
        let objHTMLScript = document.createElement('SCRIPT');
        objHTMLScript.async = pIsAsync;
        objHTMLScript.defer = pIsDefer;

        if (!!pFnOnload && typeof pFnOnload == 'function') {
            objHTMLScript.src = `https://www.google.com/recaptcha/api.js?onload=${pFnOnload.name}&render=explicit`;
        } else {
            objHTMLScript.src = 'https://www.google.com/recaptcha/api.js';
        }

        document.getElementsByTagName('head')[0].appendChild(objHTMLScript);
    }
}
